import React, { useRef, useEffect, useState, useContext } from 'react';
import {
    InputAdornment, Typography, Container, Grid, Box, Card, CardContent,
    FormControl, TextField, Radio, RadioGroup, FormControlLabel, FormLabel,
    Paper, TableContainer, Table, TableHead, TableBody, TableRow, TablePagination,
    CircularProgress, Tooltip, Button
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { AntTabs, AntTab } from '../Separaciones/tabs_style';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ContextSession } from '../../services/ContextSession';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import SearchIcon from '@mui/icons-material/Search';
import excelBoton from './excelBoton.jpg';
import * as XLSX from 'sheetjs-style';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#19438C',
        color: 'white',
        fontSize: 16,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        borderRight: 'none',
        borderLeft: 'none',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
    '&:hover': {
        backgroundColor: '#C1C1C1',
    },
}));

export default function ReversionesAplicadas() {
    const [tabIndex, setTabIndex] = React.useState(1);
    const [selectedOption, setSelectedOption] = useState('Reversiones Maestro');
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(30);
    const [searchCDRP, setSearchCDRP] = useState('');
    const [searchLote, setSearchLote] = useState('');
    const [searchObs, setSearchObs] = useState('');
    const [searchNombreParticipante, setSearchNombreParticipante] = useState('');
    const [searchUsuarioRegistro, setSearchUsuarioRegistro] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [containerWidth, setContainerWidth] = useState(0);
    const containerRef = useRef(null);
    let sessi = useContext(ContextSession);

    useEffect(() => {
        updateContainerWidth();
        window.addEventListener('resize', updateContainerWidth);
        return () => {
            window.removeEventListener('resize', updateContainerWidth);
        };
    }, []);

    useEffect(() => {
        let isMounted = true; // Bandera para verificar si el componente está montado

        const fetchData = async () => {
            setIsLoading(true);
            const estado = 1;
            const url = selectedOption === 'Reversiones Maestro'
                ? `https://bei360.injupemp.gob.hn:8444/api/SGOPER/ReversionesMaestro/?estado=${estado}`
                : `https://bei360.injupemp.gob.hn:8444/api/SGOPER/ReversionesMovimientos/?estado=${estado}`;

            try {
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${sessi.sessionData.token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const result = await response.json();
                if (isMounted) { // Solo actualizar el estado si el componente está montado
                    console.log('Datos obtenidos de la API:', result);
                    setData(result);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                if (isMounted) { // Solo actualizar el estado si el componente está montado
                    setIsLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false; // Limpiar la bandera cuando el componente se desmonte
        };
    }, [selectedOption]); // Dependencia: selectedOption

    useEffect(() => {
        const filtered = data.filter((row) => {
            const cdrpMatch = searchCDRP ? row.cdrp.toString().includes(searchCDRP) : true;
            const loteMatch = searchLote ? (row.id_documento_reversion ? row.id_documento_reversion.toString().includes(searchLote) : false) : true;
            const ObsMatch = searchObs
                ? row.observacion_documento.toLowerCase().includes(searchObs.toLowerCase())
                : true;
            const nombreMatch = searchNombreParticipante
                ? row.nombre_participante.toLowerCase().includes(searchNombreParticipante.toLowerCase())
                : true;
            const usuarioMatch = searchUsuarioRegistro
                ? row.usuario_registro.toLowerCase().includes(searchUsuarioRegistro.toLowerCase())
                : true;
            return cdrpMatch && nombreMatch && usuarioMatch && ObsMatch && loteMatch;
        });
        setFilteredData(filtered);
    }, [data, searchCDRP, searchNombreParticipante, searchUsuarioRegistro, searchObs, searchLote]);

    const updateContainerWidth = () => {
        if (containerRef.current) {
            const width = containerRef.current.clientWidth;
            setContainerWidth(width);
        }
    };

    const handleChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const handleRadioChange = (event) => {
        if (!isLoading) {
            setSelectedOption(event.target.value);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Función mejorada para exportar a Excel
    const exportToExcel = () => {
        const fields = selectedOption === 'Reversiones Maestro' ? maestroFields : movimientoFields;

        // Columnas numéricas que deben sumarse
        const numericColumns = [
            'Ajuste De Cotizacion Individual',
            'Sueldos',
            'Intereses',
            'Ajuste De Aporte Patronal',
            ...(selectedOption === 'Reversiones Movimiento' ? ['Sueldos'] : [])
        ];

        // Mapeo de columnas a índices
        const columnIndices = {};
        fields.forEach((field, index) => {
            columnIndices[field.label] = index;
        });

        // Datos para exportar
        const dataToExport = filteredData.map(row => {
            const exportedRow = {};
            fields.forEach(field => {
                if (field.key === 'ramo') {
                    exportedRow[field.label] = parseInt(row[field.key]);
                } else if (numericColumns.includes(field.label)) {
                    exportedRow[field.label] = Number(row[field.key]) || 0;
                } else {
                    exportedRow[field.label] = row[field.key] || '';
                }
            });
            return exportedRow;
        });

        // Crear hoja vacía
        const ws = XLSX.utils.aoa_to_sheet([]);

        // 1. Encabezado multilinea con colspan igual al número de columnas
        const numCols = fields.length;
        const headerRows = [
            ["DEPARTAMENTO DE OPERACIONES"],
            ["INSTITUTO NACIONAL DE JUBILACIONES Y PENSIONES DE LOS EMPLEADOS Y"],
            ["FUNCIONARIOS DEL PODER EJECUTIVO"],
            ["PLANILLA DE REVERSIONES APLICADAS"],
        ];

        // Agrega cada línea del encabezado como una fila, fusionando celdas
        headerRows.forEach((row, idx) => {
            XLSX.utils.sheet_add_aoa(ws, [row], { origin: { r: idx, c: 0 } });
            const merge = { s: { r: idx, c: 0 }, e: { r: idx, c: numCols - 1 } };
            if (!ws["!merges"]) ws["!merges"] = [];
            ws["!merges"].push(merge);
            for (let c = 0; c < numCols; c++) {
                const cellRef = XLSX.utils.encode_cell({ r: idx, c });
                if (ws[cellRef]) {
                    ws[cellRef].s = {
                        font: { bold: true, sz: 14 },
                        alignment: { horizontal: "center", vertical: "center", wrapText: true },
                    };
                }
            }
        });

        // 2. Encabezados de la tabla (fila siguiente)
        const headerRowIdx = headerRows.length;
        const headers = fields.map(field => field.label);
        XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: headerRowIdx, c: 0 } });

        // 3. Agregar datos
        XLSX.utils.sheet_add_json(ws, dataToExport, {
            origin: { r: headerRowIdx + 1, c: 0 },
            header: fields.map(f => f.label),
            skipHeader: true
        });

        // 4. Fila de totales
        const lastDataRow = dataToExport.length + headerRowIdx + 1;
        const totalRow = {};
        fields.forEach(field => {
            if (numericColumns.includes(field.label)) {
                const colLetter = XLSX.utils.encode_col(columnIndices[field.label]);
                totalRow[field.label] = {
                    f: `SUM(${colLetter}${headerRowIdx + 2}:${colLetter}${lastDataRow})`
                };
            } else if (field.label === 'Cotizante') {
                totalRow[field.label] = 'TOTAL';
            } else {
                totalRow[field.label] = '';
            }
        });
        XLSX.utils.sheet_add_json(ws, [totalRow], {
            origin: { r: lastDataRow, c: 0 },
            header: fields.map(f => f.label),
            skipHeader: true
        });

        // Ajustar rango completo
        const fullRange = {
            s: { r: 0, c: 0 },
            e: { r: lastDataRow, c: fields.length - 1 }
        };
        ws['!ref'] = XLSX.utils.encode_range(fullRange);

        // Aplicar estilos a headers y totales
        for (let R = headerRowIdx; R <= fullRange.e.r; ++R) {
            for (let C = 0; C <= fullRange.e.c; ++C) {
                const cell_address = { c: C, r: R };
                const cell_ref = XLSX.utils.encode_cell(cell_address);
                if (!ws[cell_ref]) continue;

                const isHeader = R === headerRowIdx; // Encabezados de la tabla
                const isTotalRow = R === fullRange.e.r;
                const columnLabel = fields[C]?.label;

                // Formato numérico
                if (numericColumns.includes(columnLabel)) {
                    if (!isHeader) {
                        ws[cell_ref].t = 'n';
                        ws[cell_ref].z = '#,##0.00';
                    }
                }
                ws[cell_ref].s = {
                    border: {
                        top: { style: "thin", color: { rgb: "000000" } },
                        bottom: { style: "thin", color: { rgb: "000000" } },
                        left: { style: "thin", color: { rgb: "000000" } },
                        right: { style: "thin", color: { rgb: "000000" } },
                    },
                    alignment: {
                        horizontal: numericColumns.includes(columnLabel) && !isHeader ? "right" : "center",
                        vertical: "center",
                        wrapText: true
                    },
                    ...(isHeader && {
                        font: { bold: true, color: { rgb: "FFFFFF" } },
                        fill: { fgColor: { rgb: "6BC5D0" } }
                    }),
                    ...(isTotalRow && {
                        font: { bold: true },
                        fill: { fgColor: { rgb: "D9D9D9" } }
                    })
                };
            }
        }

        // Ajustar ancho de columnas
        // Ajustar ancho de columnas SOLO usando los datos, no el header
        ws['!cols'] = fields.map(field => ({
            wch: Math.max(
                ...filteredData.map(row => String(row[field.key] || '').length),
                13 // un mínimo razonable, ajusta a tu gusto
            )
        }));


        // Crear libro y exportar
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Datos");
        const date = new Date();
        const dateStr = date.toISOString().split('T')[0];
        const fileName = `Aplicadas_${selectedOption.replace(' ', '_')}_${dateStr}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const handleSearchCDRPChange = (event) => setSearchCDRP(event.target.value);
    const handleSearchLoteChange = (event) => setSearchLote(event.target.value);
    const handleSearchObsChange = (event) => setSearchObs(event.target.value);
    const handleSearchNombreParticipanteChange = (event) => setSearchNombreParticipante(event.target.value);
    const handleSearchUsuarioRegistroChange = (event) => setSearchUsuarioRegistro(event.target.value);

    const maestroFields = [
        { key: 'nombre_participante', label: 'Nombre De Participante' },
        { key: 'cdrp', label: 'Cotizante No.' },
        { key: 'ramo', label: 'Ramo' },
        { key: 'ajuste_individual', label: 'Ajuste De Cotizacion Individual' },
        { key: 'intereses', label: 'Intereses' },
        { key: 'sueldos', label: 'Sueldos' },
        { key: 'ajuste_aporte_patronal', label: 'Ajuste De Aporte Patronal' },
        { key: 'id_documento_reversion', label: 'Lote' },
        { key: 'observacion_actualizado', label: 'Observación de Ejecución' },
        { key: 'observacion_documento', label: 'Observación' },
        { key: 'usuario_registro', label: 'Usuario Registro' },
    ];

    const movimientoFields = [
        { key: 'nombre_participante', label: 'Nombre De Participante' },
        { key: 'cdrp', label: 'Cotizante No.' },
        { key: 'ramo', label: 'Ramo' },
        { key: 'ajuste_individual', label: 'Ajuste De Cotizacion Individual' },
        { key: 'intereses', label: 'Intereses' },
        { key: 'sueldos', label: 'Sueldos' },
        { key: 'ajuste_aporte_patronal', label: 'Ajuste De Aporte Patronal' },
        { key: 'fecha_inicial', label: 'Fecha Inicial' },
        { key: 'fecha_final', label: 'Fecha Final' },
        { key: 'id_documento_reversion', label: 'Lote' },
        { key: 'observacion_actualizado', label: 'Observación de Ejecución' },
        { key: 'observacion_documento', label: 'Observación' },
        { key: 'usuario_registro', label: 'Usuario Registro' },
    ];

    return (
        <>
            <Grid container direction="column" alignItems="center">
                <Container>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <h2 style={{ marginRight: '8px', display: 'flex', alignItems: 'center' }}>
                            <CheckCircleIcon style={{ color: 'green', marginRight: '8px' }} />
                            Historial de Reversiones Aplicadas
                            <CheckCircleIcon style={{ color: 'green', marginLeft: '8px' }} />
                        </h2>
                    </div>
                </Container>

                <Container>
                    <Box sx={{ position: 'sticky', top: 0, zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Paper square>
                            <AntTabs
                                value={tabIndex}
                                onChange={handleChange}
                                indicatorColor="primary"
                                textColor="primary"
                                variant="scrollable"
                                scrollButtons="auto"
                                TabIndicatorProps={{
                                    style: { display: 'none' },
                                }}
                            >
                                <AntTab
                                    label="Reversiones Pendientes"
                                    sx={{
                                        textDecoration: tabIndex === 0 ? 'underline' : 'none',
                                    }}
                                />
                                <AntTab
                                    label="Reversiones Aplicadas"
                                    sx={{
                                        textDecoration: tabIndex === 1 ? 'underline' : 'none',
                                    }}
                                />
                                <AntTab
                                    label="Reversiones Rechazadas"
                                    sx={{
                                        textDecoration: tabIndex === 2 ? 'underline' : 'none',
                                    }}
                                />
                            </AntTabs>
                        </Paper>
                    </Box>

                    <Box mt={3}>
                        {tabIndex === 0 && navigate('/Operaciones/ReversionesPendientes')}
                        {tabIndex === 1}
                        {tabIndex === 2 && navigate('/Operaciones/ReversionesRechazadas')}
                    </Box>
                </Container>
            </Grid>

            <Box sx={{ position: 'sticky', top: 0, zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <FormControl>
                    <FormLabel id="demo-row-radio-buttons-group-label" sx={{ textAlign: 'center' }}>
                        Seleccione una opción:
                    </FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                        value={selectedOption}
                        onChange={handleRadioChange}
                        sx={{ justifyContent: 'center' }}
                    >
                        <FormControlLabel
                            value="Reversiones Maestro"
                            control={<Radio disabled={isLoading} />} // Deshabilitar si hay carga
                            label="Reversiones Maestro"
                        />
                        <FormControlLabel
                            value="Reversiones Movimiento"
                            control={<Radio disabled={isLoading} />} // Deshabilitar si hay carga
                            label="Reversiones Movimiento"
                        />
                    </RadioGroup>
                </FormControl>
            </Box>

            <Box mt={3} sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <TextField
                    placeholder="Buscar por Cotizante"
                    variant="outlined"
                    value={searchCDRP}
                    onChange={handleSearchCDRPChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    placeholder="Buscar por Lote"
                    variant="outlined"
                    value={searchLote}
                    onChange={handleSearchLoteChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    placeholder="Buscar por Participante"
                    variant="outlined"
                    value={searchNombreParticipante}
                    onChange={handleSearchNombreParticipanteChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    placeholder="Buscar por Usuario"
                    variant="outlined"
                    value={searchUsuarioRegistro}
                    onChange={handleSearchUsuarioRegistroChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    placeholder="Buscar por Observación"
                    variant="outlined"
                    value={searchObs}
                    onChange={handleSearchObsChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Botón flotante en esquina superior derecha */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 10,
                    right: 10,
                    zIndex: 9999,
                    marginTop: '90px',
                }}
            >
                <Tooltip title="Exportar a Excel" arrow placement='left'>
                    <Button
                        variant="contained"
                        onClick={exportToExcel}
                        title="Exportar a Excel"
                        sx={{
                            minWidth: 'auto',
                            padding: '2px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: 'rgb(199, 199, 199)',
                            },
                        }}
                    >
                        <img
                            src={excelBoton}
                            alt="Exportar a Excel"
                            style={{
                                height: '90px',
                                width: '90px',
                                cursor: 'pointer',
                            }}
                        />
                    </Button>
                </Tooltip>
            </Box>

            <Box mt={3}>
                {isLoading ? ( // Mostrar CircularProgress si isLoading es true
                    <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <StyledTableRow>
                                    {selectedOption === 'Reversiones Maestro'
                                        ? maestroFields.map((field) => (
                                            <StyledTableCell key={field.key}><b>{field.label}</b></StyledTableCell>
                                        ))
                                        : movimientoFields.map((field) => (
                                            <StyledTableCell key={field.key}><b>{field.label}</b></StyledTableCell>
                                        ))}
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row) => (
                                        <StyledTableRow key={row.id}>
                                            {(selectedOption === 'Reversiones Maestro' ? maestroFields : movimientoFields).map((field) => (
                                                <StyledTableCell key={field.key}>
                                                    {field.key === 'ramo'
                                                        ? parseInt(row[field.key]) // <- aquí se eliminan los decimales
                                                        : row[field.key]}
                                                </StyledTableCell>
                                            ))}
                                        </StyledTableRow>
                                    ))}
                            </TableBody>

                        </Table>
                    </TableContainer>
                )}
                <TablePagination
                    rowsPerPageOptions={[30, 40, 50]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    labelRowsPerPage={"Filas por página"}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    );
}